import { useCallback, useRef, useState } from 'react';
import { usePostVoicePreviewMutation } from '@circuitry-ai/doc-data';
import { CtyShowToast, checkType } from '@circuitry-ai/doc-ui';

export interface PlayVoicePreviewParams {
  key: string;
  voiceId: string;
  transcript: string;
  provider?: string;
}

function isAbortError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  if ('name' in error && (error as { name: string }).name === 'AbortError') return true;
  if ('message' in error) {
    return String((error as { message: string }).message).toLowerCase().includes('abort');
  }
  return false;
}

export function useVoicePreview() {
  const [postVoicePreview] = usePostVoicePreviewMutation();
  const [previewingKey, setPreviewingKey] = useState<string | null>(null);
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const previewMutationRef = useRef<{ abort: () => void } | null>(null);

  const stopPreview = useCallback(() => {
    previewMutationRef.current?.abort();
    previewMutationRef.current = null;
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    setPreviewingKey(null);
    setLoadingKey(null);
  }, []);

  const playPreview = useCallback(
    async ({ key, voiceId, transcript, provider }: PlayVoicePreviewParams) => {
      const trimmedVoiceId = voiceId.trim();
      if (!trimmedVoiceId) {
        CtyShowToast(checkType.error, 'Please select a voice first.');
        return;
      }

      const transcriptText = transcript.trim();
      if (!transcriptText) {
        CtyShowToast(checkType.error, 'Preview text is required.');
        return;
      }

      const isActive = previewingKey === key || loadingKey === key;
      if (isActive) {
        stopPreview();
        return;
      }

      stopPreview();
      setLoadingKey(key);

      const promise = postVoicePreview({
        transcript: transcriptText,
        voiceId: trimmedVoiceId,
        provider,
      });
      previewMutationRef.current = promise;

      try {
        const blob = await promise.unwrap();
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        previewAudioRef.current = audio;

        audio.onended = () => {
          setPreviewingKey(null);
          URL.revokeObjectURL(url);
          previewAudioRef.current = null;
        };

        await audio.play();
        setLoadingKey(null);
        setPreviewingKey(key);
      } catch (error: unknown) {
        if (isAbortError(error)) {
          setPreviewingKey(null);
          setLoadingKey(null);
          return;
        }

        const message =
          (error as { data?: { detail?: string }; error?: string })?.data?.detail ??
          (error as { error?: string })?.error ??
          (error as { message?: string })?.message ??
          'Could not play voice preview.';
        CtyShowToast(checkType.error, String(message));
        setPreviewingKey(null);
        setLoadingKey(null);
      } finally {
        if (previewMutationRef.current === promise) {
          previewMutationRef.current = null;
        }
      }
    },
    [loadingKey, postVoicePreview, previewingKey, stopPreview]
  );

  return { playPreview, previewingKey, loadingKey, stopPreview };
}
